import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useId, useState } from "react";
import { FileIcon, Download, X } from "lucide-react";
import { FORM_FILE_EXTENSIONS, FORM_FILE_MAX_SIZE_MB, FORM_FILE_MIME_TYPES, } from "@f2e/shared/constants";
import { Button, Input, Label } from "../../../components/base";
import { useUiTranslation } from "../../../i18n";
import { FieldDescription, FormImageLoaderContext } from "../FieldDescription";
export const FileField = ({ field, value, disabled, error, onChange, }) => {
    const { t } = useUiTranslation();
    const id = useId();
    const loadFile = useContext(FormImageLoaderContext);
    const [localError, setLocalError] = useState();
    const [downloading, setDownloading] = useState(false);
    const selected = value instanceof File ? value : null;
    const uploaded = value && typeof value === "object" && "attachmentId" in value
        ? value
        : null;
    const file = selected ?? uploaded;
    const message = localError ?? error;
    const download = async () => {
        if (!uploaded)
            return;
        setDownloading(true);
        setLocalError(undefined);
        let url;
        try {
            url = loadFile
                ? await loadFile({
                    id: uploaded.attachmentId,
                    uuid: uploaded.attachmentId,
                    name: uploaded.name,
                    type: uploaded.contentType,
                    url: uploaded.url,
                    status: "uploaded",
                })
                : uploaded.url;
            const link = document.createElement("a");
            link.href = url;
            link.download = uploaded.name;
            link.click();
        }
        catch {
            setLocalError(t("form.file.downloadFailed"));
        }
        finally {
            if (url?.startsWith("blob:"))
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            setDownloading(false);
        }
    };
    return (_jsxs("div", { className: "space-y-2 min-w-0", children: [_jsxs(Label, { htmlFor: id, className: "text-xs", children: [field.question, field.required && _jsx("span", { className: "text-destructive", children: " *" })] }), _jsx(FieldDescription, { ...field }), _jsx("p", { id: `${id}-hint`, className: "text-xs text-muted-foreground", children: t("form.file.hint") }), file ? (_jsxs("div", { className: "flex min-w-0 items-center gap-2 rounded-md border p-3", children: [_jsx(FileIcon, { className: "h-4 w-4 shrink-0 text-muted-foreground", "aria-hidden": "true" }), _jsx("span", { className: "min-w-0 flex-1 break-all text-sm", children: file.name }), uploaded ? (_jsx(Button, { type: "button", size: "icon", variant: "ghost", disabled: downloading, "aria-label": t("form.file.download"), onClick: download, children: _jsx(Download, { className: "h-4 w-4" }) })) : null, !disabled && (_jsx(Button, { type: "button", size: "icon", variant: "ghost", "aria-label": t("form.file.remove"), onClick: () => {
                            onChange?.(null);
                            setLocalError(undefined);
                        }, children: _jsx(X, { className: "h-4 w-4" }) }))] })) : null, !file && (_jsx(Input, { id: id, type: "file", disabled: disabled, "aria-required": field.required, "aria-invalid": !!message, "aria-describedby": `${id}-hint${message ? ` ${id}-error` : ""}`, accept: [...FORM_FILE_MIME_TYPES, ...FORM_FILE_EXTENSIONS].join(","), onChange: (event) => {
                    const next = event.target.files?.[0];
                    if (!next)
                        return;
                    const validationError = validateFileField(field, next, t);
                    setLocalError(validationError);
                    onChange?.(next);
                    event.target.value = "";
                } })), message && (_jsx("p", { id: `${id}-error`, role: "alert", className: "text-xs text-destructive", children: message }))] }));
};
export const validateFileField = (field, value, t) => {
    if (value instanceof File) {
        if (value.size === 0)
            return t("form.file.empty");
        if (value.size > FORM_FILE_MAX_SIZE_MB * 1024 * 1024)
            return t("form.file.tooLarge");
        const extension = `.${value.name.split(".").pop()?.toLowerCase()}`;
        const supportedMime = FORM_FILE_MIME_TYPES.some((type) => type === value.type ||
            (type === "text/*" && value.type.startsWith("text/")));
        if (!supportedMime ||
            !FORM_FILE_EXTENSIONS.some((type) => type === extension))
            return t("form.file.unsupported");
        return undefined;
    }
    if (value && typeof value === "object" && "attachmentId" in value)
        return undefined;
    return field.required ? t("form.validation.required") : undefined;
};
//# sourceMappingURL=FileField.js.map